import type { ResellerLicence } from "@xop-any-time/shared";
import { applianceLicenceStatus } from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Copy, Download, Eye, MoreHorizontal, Search } from "lucide-react";
import { useMemo, useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";

import { usePermissions } from "../../../auth/usePermissions.js";
import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { PageLoader } from "../../../components/ui/page-loader.js";
import { Pagination } from "../../../components/ui/pagination.js";
import { SortableHeader } from "../../../crm/components/table/SortableHeader.js";
import { isPermissionDenied } from "../../../lib/api.js";
import { keys, listLicences } from "../../../lib/crmApi.js";
import { PageHeader } from "../crm.js";
import { CreateLicenceDialog } from "./CreateLicenceDialog.js";
import { LicenceKindChip, LicenceRenewalChip, LicenceStatusChip, LicenceWorkflowChip, takeLicenceFile } from "./licence-ui.js";

/**
 * Licences — every APPLIANCE in this reseller's subtree, as the COMMAND
 * CENTER'S CRM ▸ Licences table lays it out (2026-09-14): the same columns
 * (Customer with its label beneath, Ports, Expires, Term, Workflow, File), the
 * same search box and filter toggles, the same sortable headers and the same
 * three-dots row menu. Two things are deliberately absent, and they are the
 * whole difference between the two portals:
 *
 * - **No checkboxes, no Delete** — singly or in bulk. A reseller licenses its
 *   customers' boxes; it does not remove a licence, and the row menu has no
 *   such item to find.
 * - **No Approve.** A FULL licence waits for approval by XOP's executives;
 *   the reseller asks, it never answers.
 *
 * SCOPE IS NOT DECIDED HERE: crm-api narrows the list to this reseller's own
 * subtree through the same scope every other read uses, and this page renders
 * whatever it is given. The whole scope arrives in one read, so search,
 * sorting and paging are done in the browser. The default order is the
 * server's: soonest expiry first, perpetual last — a licence with no end date
 * is not "expiring furthest away", it is a different kind of thing.
 */
type Filter = "all" | "attention" | "awaiting";
type SortKey = "customer" | "ports" | "expires" | "term" | "workflow" | "file";
type SortDir = "asc" | "desc";

const PER_PAGE = 25;

function ports(licence: ResellerLicence): string {
  return `${licence.ports.audioConferencing} audio / ${licence.ports.webConferencing} web`;
}

/** The Expires column: the date itself, or the word for its absence. The
 * TERM column beside it says how much of it is left. */
export function expiryLabel(licence: Pick<ResellerLicence, "expiresOn">): string {
  return licence.expiresOn ?? "No end date";
}

const TERM_ORDER: Record<ReturnType<typeof applianceLicenceStatus>, number> = {
  expired: 0, expiring: 1, active: 2, perpetual: 3,
};
const WORKFLOW_ORDER: Record<ResellerLicence["approval"]["status"], number> = {
  pending: 0, rejected: 1, not_required: 2, approved: 3,
};

/** One comparator per column — the command center's, verbatim. */
function compare(a: ResellerLicence, b: ResellerLicence, key: SortKey): number {
  switch (key) {
    case "customer":
      return a.customerName.localeCompare(b.customerName) || a.label.localeCompare(b.label);
    case "ports":
      return (
        a.ports.audioConferencing - b.ports.audioConferencing ||
        a.ports.webConferencing - b.ports.webConferencing
      );
    case "expires":
      if (a.expiresOn === null && b.expiresOn === null) return 0;
      if (a.expiresOn === null) return 1;
      if (b.expiresOn === null) return -1;
      return a.expiresOn.localeCompare(b.expiresOn);
    case "term":
      return TERM_ORDER[applianceLicenceStatus(a)] - TERM_ORDER[applianceLicenceStatus(b)];
    case "workflow":
      return WORKFLOW_ORDER[a.approval.status] - WORKFLOW_ORDER[b.approval.status];
    case "file":
      return Number(Boolean(b.currentIssue)) - Number(Boolean(a.currentIssue));
  }
}

/** Perpetual sorts LAST under "expires" both ways on purpose — it is a kind,
 * not a date. Exported so the rule is pinned without mounting the page. */
export function sortLicences(rows: readonly ResellerLicence[], key: SortKey, dir: SortDir): ResellerLicence[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (key === "expires") {
      if (a.expiresOn === null && b.expiresOn !== null) return 1;
      if (b.expiresOn === null && a.expiresOn !== null) return -1;
    }
    return compare(a, b, key) * sign;
  });
}

/** The search: customer, label, hardware address (what tells two of one
 * customer's boxes apart) and feature names. */
export function matchesLicenceSearch(licence: ResellerLicence, needle: string): boolean {
  if (!needle) return true;
  return (
    licence.customerName.toLowerCase().includes(needle) ||
    licence.label.toLowerCase().includes(needle) ||
    licence.hardwareAddress.toLowerCase().includes(needle) ||
    licence.features.some((feature) => feature.name.toLowerCase().includes(needle))
  );
}

export function LicencesPage(): JSX.Element {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<SortKey>("expires");
  const [dir, setDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ el: HTMLElement; id: number } | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: keys.licences(),
    queryFn: () => listLicences(),
  });

  const all = data?.licences ?? [];

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = all.filter((licence) => {
      if (filter === "attention") {
        const status = applianceLicenceStatus(licence);
        if (status !== "expiring" && status !== "expired") return false;
      }
      if (filter === "awaiting" && licence.approval.status !== "pending") return false;
      return matchesLicenceSearch(licence, needle);
    });
    return sortLicences(rows, sort, dir);
  }, [all, search, filter, sort, dir]);

  const pageRows = useMemo(
    () => visible.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [visible, page],
  );

  const toggleSort = (column: string): void => {
    const key = column as SortKey;
    if (key === sort) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      setDir("asc");
    }
    setPage(1);
  };

  async function download(licence: ResellerLicence): Promise<void> {
    setBusy(licence.id);
    setActionError(null);
    try {
      await takeLicenceFile(licence);
    } catch (err) {
      // The refusal's sentence, not a broken download: "Ask an administrator
      // to raise the limit" is something a reseller can act on.
      setActionError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const menuRow = menu ? all.find((row) => row.id === menu.id) : undefined;

  if (isPermissionDenied(error)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Licences" />
        <NoAccessPanel permission={error.permission ?? "licences:view"} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Title and button, no prose — the table says what a licence is better
          than a sentence above it (the command center's own rule). */}
      <PageHeader
        title="Licences"
        actions={
          can("licences:edit") ? (
            <Button variant="contained" onClick={() => setCreating(true)}>
              New licence
            </Button>
          ) : undefined
        }
      />
      <CreateLicenceDialog open={creating} onClose={() => setCreating(false)} />
      {actionError ? (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search customer, label, hardware address or feature"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              inputProps={{ "aria-label": "Search licences" }}
              sx={{ minWidth: 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(_event, next: Filter | null) => {
                if (next) {
                  setFilter(next);
                  setPage(1);
                }
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="attention">Expiring or expired</ToggleButton>
              <ToggleButton value="awaiting">Awaiting approval</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {isPending ? (
            <PageLoader />
          ) : error ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography color="error">{(error as Error).message}</Typography>
            </Box>
          ) : visible.length === 0 ? (
            <Typography sx={{ py: 6, textAlign: "center" }} color="text.secondary">
              {all.length === 0
                ? "No appliance has a licence yet."
                : filter === "attention"
                  ? "Nothing is expiring in the next 60 days."
                  : filter === "awaiting"
                    ? "Nothing is waiting for approval."
                    : "No licence matches that search."}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableHeader column="customer" label="Customer" sort={sort} dir={dir} onSort={toggleSort} />
                    <SortableHeader column="ports" label="Ports" sort={sort} dir={dir} onSort={toggleSort} />
                    <SortableHeader column="expires" label="Expires" sort={sort} dir={dir} onSort={toggleSort} />
                    <SortableHeader column="term" label="Term" sort={sort} dir={dir} onSort={toggleSort} />
                    <SortableHeader column="workflow" label="Workflow" sort={sort} dir={dir} onSort={toggleSort} />
                    <SortableHeader column="file" label="File" sort={sort} dir={dir} onSort={toggleSort} align="right" />
                    <TableCell align="right" sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((licence) => (
                    <TableRow
                      key={licence.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/licences/${licence.id}`)}
                    >
                      <TableCell>
                        {licence.customerName}
                        {licence.label ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {licence.label}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{ports(licence)}</TableCell>
                      <TableCell>
                        {expiryLabel(licence)}
                        {/* What comes next — a renewal drafted, waiting,
                            generated, ready, or the day the window opens. */}
                        <Box sx={{ mt: 0.5 }}>
                          <LicenceRenewalChip licence={licence} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          <LicenceStatusChip licence={licence} />
                          <LicenceKindChip licence={licence} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <LicenceWorkflowChip licence={licence} />
                      </TableCell>
                      <TableCell align="right">
                        {licence.currentIssue ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy === licence.id}
                            // The row opens the builder; taking the file must not also navigate.
                            onClick={(event) => {
                              event.stopPropagation();
                              void download(licence);
                            }}
                          >
                            Download
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          sx={{ width: 32, height: 32 }}
                          aria-label="Open menu"
                          onClick={(event) => {
                            event.stopPropagation();
                            setMenu({ el: event.currentTarget, id: licence.id });
                          }}
                        >
                          <MoreHorizontal size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={page}
                perPage={PER_PAGE}
                totalCount={visible.length}
                onPageChange={setPage}
                entityNoun="licences"
                hideIfSinglePage
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ONE menu for whichever row opened it — the CRM's three-dots
          convention. Open, Download, Duplicate: there is no Delete and no
          Approve here, and that is the point. */}
      <Menu
        anchorEl={menu?.el ?? null}
        open={menu !== null}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
        <MenuItem
          onClick={() => {
            setMenu(null);
            if (menuRow) navigate(`/licences/${menuRow.id}`);
          }}
        >
          <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
          Open
        </MenuItem>
        {menuRow?.currentIssue ? (
          <MenuItem
            onClick={() => {
              setMenu(null);
              if (menuRow) void download(menuRow);
            }}
          >
            <Box sx={{ mr: 1, display: "flex" }}><Download size={16} /></Box>
            Download file
          </MenuItem>
        ) : null}
        {can("licences:edit") ? <Divider /> : null}
        {can("licences:edit") ? (
          <MenuItem
            onClick={() => {
              setMenu(null);
              if (menuRow) navigate(`/licences/new?from=${menuRow.id}`);
            }}
          >
            <Box sx={{ mr: 1, display: "flex" }}><Copy size={16} /></Box>
            Duplicate
          </MenuItem>
        ) : null}
      </Menu>
    </Box>
  );
}

export default LicencesPage;
